import { logAnalyticsEvent, recordVisit } from './api'
import { sendAdsConversion, sendGtagEvent } from './gtag'

const SESSION_STORAGE_KEY = 'artist_analytics_session_v1'
const VISIT_LOG_KEY = 'artist_visit_logged_v2'
const COUNTRY_STORAGE_KEY = 'artist_last_country'
const UTM_STORAGE_KEY = 'artist_landing_utm'
const SESSION_TTL_MS = 1000 * 60 * 60 * 24 // 24 hours
const UTM_TTL_MS = 1000 * 60 * 60 * 24 * 30 // 30 days

type StoredSession = { id: string; expiresAt: number }

const readJson = <T>(raw: string | null): T | null => {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch (_error) {
    return null
  }
}

const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random()}`
}

export const getAnalyticsSessionId = () => {
  if (typeof window === 'undefined') return ''
  const now = Date.now()
  const existing = readJson<StoredSession>(localStorage.getItem(SESSION_STORAGE_KEY))
  if (existing?.id && existing.expiresAt > now) {
    return existing.id
  }
  const next = { id: generateId(), expiresAt: now + SESSION_TTL_MS }
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next))
  return next.id
}

const getLandingUtmParams = () => {
  if (typeof window === 'undefined') return {}
  const stored = readJson<{ params: Record<string, string>; capturedAt: number }>(
    localStorage.getItem(UTM_STORAGE_KEY),
  )
  if (stored?.params && stored.capturedAt + UTM_TTL_MS > Date.now()) {
    return stored.params
  }
  const params = new URLSearchParams(window.location.search)
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
  const captured: Record<string, string> = {}
  utmKeys.forEach((key) => {
    const value = params.get(key)
    if (value) {
      captured[key] = value
    }
  })
  if (Object.keys(captured).length) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({ params: captured, capturedAt: Date.now() }))
  }
  return captured
}

const buildDeviceSnapshot = () => {
  if (typeof navigator === 'undefined') {
    return {}
  }
  const screen = typeof window !== 'undefined' ? window.screen : undefined
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screen: screen
      ? {
          width: screen.width,
          height: screen.height,
        }
      : undefined,
  }
}

export const ensureVisitRecorded = async () => {
  if (typeof window === 'undefined') return
  const today = new Date().toISOString().slice(0, 10)
  const alreadyLogged = localStorage.getItem(VISIT_LOG_KEY)
  if (alreadyLogged === today) return
  const sessionId = getAnalyticsSessionId()
  const utm = getLandingUtmParams()
  try {
    const result = await recordVisit({
      sessionId,
      path: window.location.pathname || '/',
      referrer: document.referrer || '',
      utm,
      device: buildDeviceSnapshot(),
      locale: navigator.language,
    })
    if (result?.country) {
      localStorage.setItem(COUNTRY_STORAGE_KEY, result.country)
      notifyCountryChange(result.country)
    }
    localStorage.setItem(VISIT_LOG_KEY, today)
  } catch (error) {
    console.error('Failed to record visit', error)
  }
}

export const getLastKnownCountry = () => {
  if (typeof window === 'undefined') return ''
  return localStorage.getItem(COUNTRY_STORAGE_KEY) || ''
}

export const trackAnalyticsEvent = (type: string, payload?: Omit<Parameters<typeof logAnalyticsEvent>[0], 'type'>) => {
  if (typeof window === 'undefined') return
  const sessionId = payload?.sessionId || getAnalyticsSessionId()
  logAnalyticsEvent({ ...payload, type, sessionId }).catch((error) => {
    if (import.meta.env.DEV) {
      console.warn('Analytics event failed', error)
    }
  })
  if (payload?.metadata) {
    sendGtagEvent(type, payload.metadata as Record<string, unknown>)
  } else {
    sendGtagEvent(type)
  }
  if (type === 'payment_success' && payload?.metadata) {
    const { orderId, amount, currency } = payload.metadata as Record<string, unknown>
    if (orderId) {
      sendAdsConversion({
        transactionId: String(orderId),
        value: typeof amount === 'number' ? amount : undefined,
        currency: typeof currency === 'string' ? currency : undefined,
      })
    }
  }
}
const notifyCountryChange = (country: string) => {
  if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return
  window.dispatchEvent(new CustomEvent('artist-country-update', { detail: country }))
}
